// Public Intake Page (Permanent Link) - /intake/:slug
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
  Scale, 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Users,
  Gavel,
  Calendar as CalendarIcon,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  File,
  Shield,
  Clock
} from 'lucide-react';
import { format, addDays, isBefore, startOfDay } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RELATIONSHIP_TYPES = [
  { id: 'child', name: 'Child' },
  { id: 'spouse', name: 'Spouse' },
  { id: 'parent', name: 'Parent' },
  { id: 'sibling', name: 'Sibling' },
  { id: 'guardian_ad_litem', name: 'Guardian ad Litem' },
  { id: 'family_member', name: 'Other Family Member' },
  { id: 'witness', name: 'Witness' },
  { id: 'expert', name: 'Expert' },
  { id: 'other', name: 'Other' }
];

export default function PublicIntakePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [intakeInfo, setIntakeInfo] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [confirmation, setConfirmation] = useState(null);
  
  // Scheduling state
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    preferred_contact_method: 'phone',
    case_type: '',
    case_description: '',
    lead_source: 'website',
    opposing_parties: [],
    other_related_parties: [],
    prior_attorneys: [],
    opposing_attorneys: [],
    known_court_dates: [],
    additional_notes: '',
    scheduled_date: null,
    scheduled_time: null
  });
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [customAnswers, setCustomAnswers] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);

  // Helper to render custom questions
  const renderCustomQuestions = () => {
    const questions = intakeInfo?.custom_questions || [];
    if (questions.length === 0) return null;
    
    return (
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium mb-3">Additional Questions</h4>
        <div className="space-y-4">
          {questions.map((q) => (
            <div key={q.id}>
              <Label>
                {q.question}
                {q.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {q.type === 'text' && (
                <Input
                  value={customAnswers[q.id] || ''}
                  onChange={(e) => setCustomAnswers({...customAnswers, [q.id]: e.target.value})}
                  required={q.required}
                  className="mt-1"
                />
              )}
              {q.type === 'textarea' && (
                <Textarea
                  value={customAnswers[q.id] || ''}
                  onChange={(e) => setCustomAnswers({...customAnswers, [q.id]: e.target.value})}
                  required={q.required}
                  rows={3}
                  className="mt-1"
                />
              )}
              {q.type === 'select' && (
                <select
                  value={customAnswers[q.id] || ''}
                  onChange={(e) => setCustomAnswers({...customAnswers, [q.id]: e.target.value})}
                  required={q.required}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                >
                  <option value="">Select an option</option>
                  {(q.options || []).map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
              )}
              {q.type === 'checkbox' && (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    checked={customAnswers[q.id] || false}
                    onChange={(e) => setCustomAnswers({...customAnswers, [q.id]: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-muted-foreground">Yes</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchIntakeInfo();
  }, [slug]);

  useEffect(() => {
    if (selectedDate && intakeInfo?.scheduling_enabled) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate]);

  const fetchIntakeInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/public/${slug}`);
      if (res.ok) {
        setIntakeInfo(await res.json());
      } else {
        const data = await res.json();
        setError(data.detail || 'Intake form not found');
      }
    } catch (err) {
      setError('Failed to load intake form');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (date) => {
    setLoadingSlots(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/intake/public/${slug}/availability/${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.available_slots);
      }
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Party/Attorney handlers (same as ClientIntakePage)
  const addOpposingParty = () => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: [...prev.opposing_parties, { name: '', relationship: 'opposing_party', notes: '' }]
    }));
  };

  const updateOpposingParty = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: prev.opposing_parties.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeOpposingParty = (index) => {
    setFormData(prev => ({
      ...prev,
      opposing_parties: prev.opposing_parties.filter((_, i) => i !== index)
    }));
  };

  const addRelatedParty = () => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: [...prev.other_related_parties, { name: '', relationship: '', notes: '' }]
    }));
  };

  const updateRelatedParty = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: prev.other_related_parties.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  const removeRelatedParty = (index) => {
    setFormData(prev => ({
      ...prev,
      other_related_parties: prev.other_related_parties.filter((_, i) => i !== index)
    }));
  };

  const addPriorAttorney = () => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: [...prev.prior_attorneys, { name: '', firm_name: '', phone: '', email: '', representing: 'client_prior' }]
    }));
  };

  const updatePriorAttorney = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: prev.prior_attorneys.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removePriorAttorney = (index) => {
    setFormData(prev => ({
      ...prev,
      prior_attorneys: prev.prior_attorneys.filter((_, i) => i !== index)
    }));
  };

  const addOpposingAttorney = () => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: [...prev.opposing_attorneys, { name: '', firm_name: '', phone: '', email: '', representing: 'opposing_party' }]
    }));
  };

  const updateOpposingAttorney = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: prev.opposing_attorneys.map((a, i) => 
        i === index ? { ...a, [field]: value } : a
      )
    }));
  };

  const removeOpposingAttorney = (index) => {
    setFormData(prev => ({
      ...prev,
      opposing_attorneys: prev.opposing_attorneys.filter((_, i) => i !== index)
    }));
  };

  const addCourtDate = () => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: [...prev.known_court_dates, { date: '', description: '', court_name: '' }]
    }));
  };

  const updateCourtDate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: prev.known_court_dates.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeCourtDate = (index) => {
    setFormData(prev => ({
      ...prev,
      known_court_dates: prev.known_court_dates.filter((_, i) => i !== index)
    }));
  };

  // File handling
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = intakeInfo?.allowed_file_types || [];
    
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`File type .${ext} is not allowed`);
        return false;
      }
      return true;
    });
    
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, intakeInfo?.max_files || 10));
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Form validation
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.first_name && formData.last_name && formData.email && formData.phone;
      case 2:
        return formData.case_type && formData.case_description;
      case 3:
      case 4:
        return true; // Optional steps
      case 5:
        // Scheduling step (if enabled) or Documents step
        if (intakeInfo?.scheduling_enabled) {
          return true; // Scheduling is optional
        }
        return true;
      case 6:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit form
  const submitIntake = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      toast.error('Please complete all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Add scheduling info if selected
      const submitFormData = { ...formData };
      if (selectedDate && selectedSlot) {
        submitFormData.scheduled_date = format(selectedDate, 'yyyy-MM-dd');
        submitFormData.scheduled_time = selectedSlot.start_time.split('T')[1].substring(0, 5);
      }

      const formPayload = new FormData();
      formPayload.append('intake_data', JSON.stringify(submitFormData));
      
      selectedFiles.forEach(file => {
        formPayload.append('files', file);
      });

      const res = await fetch(`${API_URL}/api/intake/public/${slug}`, {
        method: 'POST',
        body: formPayload
      });

      if (res.ok) {
        const result = await res.json();
        setConfirmation(result);
        toast.success('Intake form submitted successfully!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to submit intake form');
      }
    } catch (err) {
      toast.error('Failed to submit intake form');
    } finally {
      setSubmitting(false);
    }
  };

  // Disable dates in calendar
  const disabledDates = (date) => {
    const today = startOfDay(new Date());
    const maxDate = addDays(today, intakeInfo?.max_advance_days || 30);
    return isBefore(date, today) || isBefore(maxDate, date);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-center">
          <Scale className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-muted-foreground">Loading intake form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Intake Form Unavailable</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="min-h-screen bg-slate-50 py-8" data-testid="public-intake-confirmation">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="font-heading text-xl font-bold text-navy">Legal AI</span>
              <span className="font-heading text-xl font-bold text-bronze">Dynamics</span>
            </div>
          </div>

          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Intake Form Submitted!</h2>
              <p className="text-emerald-700 mb-6">{confirmation.message}</p>
              
              <div className="bg-white rounded-lg p-6 text-left mb-6">
                <h3 className="font-semibold mb-4 text-slate-800">Submission Summary</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium">{confirmation.contact_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Case Type:</span>
                    <span className="font-medium">{confirmation.case_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents:</span>
                    <span className="font-medium">{confirmation.documents_uploaded} file(s)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reference #:</span>
                    <span className="font-mono font-medium">{confirmation.reference_number}</span>
                  </div>
                  {confirmation.appointment_scheduled && (
                    <>
                      <div className="border-t pt-3 mt-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Appointment:</span>
                          <span className="font-medium text-emerald-700">
                            {confirmation.appointment_date} at {confirmation.appointment_time}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="text-sm text-emerald-700">
                <p className="mb-2"><strong>What happens next?</strong></p>
                <p>
                  {intakeInfo?.firm_name || 'The law firm'} has been notified of your submission 
                  and will contact you soon.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-6 text-sm text-muted-foreground">
            <p>Please save your reference number: <strong className="font-mono">{confirmation.reference_number}</strong></p>
          </div>
        </div>
      </div>
    );
  }

  // Step configuration - filter based on enabled sections
  const getSteps = () => {
    const sections = intakeInfo?.form_sections || {};
    const baseSteps = [];
    let stepNum = 1;
    
    // Contact Info - always first if enabled
    if (sections.contact_info?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'contact_info', title: 'Contact Info', icon: User });
    }
    
    // Case Details
    if (sections.case_details?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'case_details', title: 'Case Details', icon: Briefcase });
    }
    
    // Related Parties
    if (sections.related_parties?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'related_parties', title: 'Related Parties', icon: Users });
    }
    
    // Attorneys & Dates
    if (sections.attorneys_dates?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'attorneys_dates', title: 'Attorneys & Dates', icon: Gavel });
    }
    
    // Documents
    if (sections.documents?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'documents', title: 'Documents', icon: FileText });
    }
    
    // Scheduling (only if enabled in scheduling settings)
    if (intakeInfo?.scheduling_enabled && sections.scheduling?.enabled !== false) {
      baseSteps.push({ num: stepNum++, key: 'scheduling', title: 'Schedule', icon: CalendarIcon });
    }
    
    return baseSteps;
  };

  const steps = getSteps();
  const totalSteps = steps.length;
  
  // Get primary color from settings or default
  const primaryColor = intakeInfo?.primary_color || '#1e3a5f';

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="public-intake-page">
      {/* Header with Custom Branding */}
      <div className="max-w-3xl mx-auto px-4 mb-6">
        <div className="flex items-center gap-3">
          {intakeInfo?.firm_logo_url ? (
            <img 
              src={`${API_URL}${intakeInfo.firm_logo_url}`} 
              alt={intakeInfo.firm_name} 
              className="h-12 w-auto"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: primaryColor }}
            >
              <Scale className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold" style={{ color: primaryColor }}>
              {intakeInfo.firm_name}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        {/* Welcome Card with Custom Message */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl" style={{ color: primaryColor }}>
              {intakeInfo.firm_name}
            </CardTitle>
            <CardDescription>
              {intakeInfo?.welcome_message || (
                <>Welcome! Please complete this intake form to get started. Fields marked with <span className="text-red-500">*</span> are required.</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4" />
              <span>Your information is securely transmitted and protected.</span>
            </div>
            {intakeInfo?.privacy_policy_url && (
              <div className="mt-2">
                <a 
                  href={intakeInfo.privacy_policy_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm underline"
                  style={{ color: primaryColor }}
                >
                  View our Privacy Policy
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Custom Disclaimer */}
        {intakeInfo?.custom_disclaimer && (
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardContent className="pt-4 pb-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">{intakeInfo.custom_disclaimer}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps with Custom Color */}
        <div className="mb-6">
          <div className="flex justify-between">
            {steps.map((step) => (
              <div 
                key={step.num} 
                className="flex flex-col items-center"
                style={{ color: step.num <= currentStep ? primaryColor : '#9ca3af' }}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    step.num < currentStep ? 'text-white' :
                    step.num === currentStep ? 'border-2' :
                    'bg-slate-100'
                  }`}
                  style={{
                    backgroundColor: step.num < currentStep ? primaryColor : 
                                     step.num === currentStep ? `${primaryColor}20` : undefined,
                    borderColor: step.num === currentStep ? primaryColor : undefined,
                    color: step.num === currentStep ? primaryColor : undefined
                  }}
                >
                  {step.num < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-xs font-medium hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full transition-all duration-300"
              style={{ 
                width: `${(currentStep / totalSteps) * 100}%`,
                backgroundColor: primaryColor 
              }}
            />
          </div>
        </div>

        {/* Form Steps */}
        <Card>
          <CardContent className="pt-6">
            {/* Step 1: Contact Information */}
            {steps[currentStep - 1]?.key === 'contact_info' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" style={{ color: primaryColor }} />
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                    <Input 
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => updateFormData('first_name', e.target.value)}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name <span className="text-red-500">*</span></Label>
                    <Input 
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => updateFormData('last_name', e.target.value)}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        id="email"
                        type="email"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input 
                        id="phone"
                        type="tel"
                        className="pl-10"
                        value={formData.phone}
                        onChange={(e) => updateFormData('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Textarea 
                      id="address"
                      className="pl-10"
                      value={formData.address}
                      onChange={(e) => updateFormData('address', e.target.value)}
                      placeholder="123 Main St, City, State ZIP"
                      rows={2}
                    />
                  </div>
                </div>

                <div>
                  <Label>Preferred Contact Method <span className="text-red-500">*</span></Label>
                  <div className="flex gap-4 mt-2">
                    {['phone', 'email', 'text'].map((method) => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.preferred_contact_method === method}
                          onCheckedChange={() => updateFormData('preferred_contact_method', method)}
                        />
                        <span className="capitalize">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Custom Questions for Contact Info */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Step 2: Case Details */}
            {steps[currentStep - 1]?.key === 'case_details' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Briefcase className="w-5 h-5" style={{ color: primaryColor }} />
                  Case Details
                </h3>

                <div>
                  <Label htmlFor="case_type">Type of Legal Matter <span className="text-red-500">*</span></Label>
                  <Select value={formData.case_type} onValueChange={(v) => updateFormData('case_type', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select case type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeInfo.case_types.map((type) => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="case_description">
                    Please describe your situation <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="case_description"
                    value={formData.case_description}
                    onChange={(e) => updateFormData('case_description', e.target.value)}
                    placeholder="Please provide details about your legal matter..."
                    rows={5}
                  />
                </div>

                <div>
                  <Label htmlFor="lead_source">How did you hear about us?</Label>
                  <Select value={formData.lead_source} onValueChange={(v) => updateFormData('lead_source', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {intakeInfo.lead_sources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>{source.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Custom Questions for Case Details */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Step 3: Related Parties */}
            {steps[currentStep - 1]?.key === 'related_parties' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: primaryColor }} />
                  Related Parties
                </h3>

                {/* Opposing Parties */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Opposing Party(ies)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOpposingParty}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.opposing_parties.length === 0 && (
                    <p className="text-sm text-muted-foreground">No opposing parties added yet.</p>
                  )}
                  {formData.opposing_parties.map((party, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Opposing Party #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeOpposingParty(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Name of opposing party"
                        value={party.name}
                        onChange={(e) => updateOpposingParty(index, 'name', e.target.value)}
                      />
                      <Input 
                        placeholder="Notes"
                        value={party.notes}
                        onChange={(e) => updateOpposingParty(index, 'notes', e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Other Related Parties */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Other Related Parties</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addRelatedParty}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Include children, important family members, guardian ad litem, etc.
                  </p>
                  {formData.other_related_parties.map((party, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Related Party #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeRelatedParty(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <Input 
                        placeholder="Name"
                        value={party.name}
                        onChange={(e) => updateRelatedParty(index, 'name', e.target.value)}
                      />
                      <Select 
                        value={party.relationship} 
                        onValueChange={(v) => updateRelatedParty(index, 'relationship', v)}
                      >
                        <SelectTrigger><SelectValue placeholder="Relationship" /></SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIP_TYPES.map((rel) => (
                            <SelectItem key={rel.id} value={rel.id}>{rel.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        placeholder="Notes"
                        value={party.notes}
                        onChange={(e) => updateRelatedParty(index, 'notes', e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Custom Questions for Related Parties */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Step 4: Attorneys & Court Dates */}
            {steps[currentStep - 1]?.key === 'attorneys_dates' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Gavel className="w-5 h-5" style={{ color: primaryColor }} />
                  Attorneys & Court Dates
                </h3>

                {/* Prior Attorneys */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Prior Attorney(s)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addPriorAttorney}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.prior_attorneys.length === 0 && (
                    <p className="text-sm text-muted-foreground">No prior attorneys listed.</p>
                  )}
                  {formData.prior_attorneys.map((attorney, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Prior Attorney #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removePriorAttorney(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Name" value={attorney.name} onChange={(e) => updatePriorAttorney(index, 'name', e.target.value)} />
                        <Input placeholder="Firm" value={attorney.firm_name} onChange={(e) => updatePriorAttorney(index, 'firm_name', e.target.value)} />
                        <Input placeholder="Phone" value={attorney.phone} onChange={(e) => updatePriorAttorney(index, 'phone', e.target.value)} />
                        <Input placeholder="Email" value={attorney.email} onChange={(e) => updatePriorAttorney(index, 'email', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Opposing Attorneys */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Opposing Attorney(s)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addOpposingAttorney}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.opposing_attorneys.length === 0 && (
                    <p className="text-sm text-muted-foreground">No opposing attorneys listed.</p>
                  )}
                  {formData.opposing_attorneys.map((attorney, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-amber-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Opposing Attorney #{index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeOpposingAttorney(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input placeholder="Name" value={attorney.name} onChange={(e) => updateOpposingAttorney(index, 'name', e.target.value)} />
                        <Input placeholder="Firm" value={attorney.firm_name} onChange={(e) => updateOpposingAttorney(index, 'firm_name', e.target.value)} />
                        <Input placeholder="Phone" value={attorney.phone} onChange={(e) => updateOpposingAttorney(index, 'phone', e.target.value)} />
                        <Input placeholder="Email" value={attorney.email} onChange={(e) => updateOpposingAttorney(index, 'email', e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Court Dates */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Known Court Dates</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addCourtDate}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.known_court_dates.length === 0 && (
                    <p className="text-sm text-muted-foreground">No court dates known.</p>
                  )}
                  {formData.known_court_dates.map((courtDate, index) => (
                    <div key={index} className="p-4 border rounded-lg bg-red-50 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4" /> Court Date #{index + 1}
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCourtDate(index)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input type="date" value={courtDate.date} onChange={(e) => updateCourtDate(index, 'date', e.target.value)} />
                        <Input placeholder="Court Name" value={courtDate.court_name} onChange={(e) => updateCourtDate(index, 'court_name', e.target.value)} />
                      </div>
                      <Input placeholder="Description" value={courtDate.description} onChange={(e) => updateCourtDate(index, 'description', e.target.value)} />
                    </div>
                  ))}
                </div>
                
                {/* Custom Questions for Attorneys & Dates */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Scheduling Step */}
            {steps[currentStep - 1]?.key === 'scheduling' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" style={{ color: primaryColor }} />
                  Schedule Your Consultation
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select a date and time for your initial consultation. This step is optional.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div>
                    <Label className="mb-2 block">Select a Date</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDates}
                      className="rounded-md border"
                    />
                  </div>

                  {/* Time Slots */}
                  <div>
                    <Label className="mb-2 block">
                      Available Times {selectedDate && `- ${format(selectedDate, 'MMM d, yyyy')}`}
                    </Label>
                    
                    {!selectedDate && (
                      <div className="p-8 text-center text-muted-foreground border rounded-lg">
                        <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>Select a date to see available times</p>
                      </div>
                    )}

                    {selectedDate && loadingSlots && (
                      <div className="p-8 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primaryColor }} />
                        <p className="text-muted-foreground mt-2">Loading availability...</p>
                      </div>
                    )}

                    {selectedDate && !loadingSlots && availableSlots.length === 0 && (
                      <div className="p-8 text-center text-muted-foreground border rounded-lg">
                        <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No available times on this date</p>
                        <p className="text-sm">Please select another date</p>
                      </div>
                    )}

                    {selectedDate && !loadingSlots && availableSlots.length > 0 && (
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {availableSlots.map((slot, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={`w-full p-3 rounded-lg border-2 text-left transition-all`}
                            style={{
                              borderColor: selectedSlot === slot ? primaryColor : '#e2e8f0',
                              backgroundColor: selectedSlot === slot ? `${primaryColor}10` : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedSlot !== slot) {
                                e.target.style.borderColor = `${primaryColor}50`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedSlot !== slot) {
                                e.target.style.borderColor = '#e2e8f0';
                              }
                            }}
                          >
                            <div className="font-medium">{slot.display_time}</div>
                            <div className="text-sm text-muted-foreground">
                              {intakeInfo.appointment_duration} min consultation
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedSlot && (
                      <div 
                        className="mt-4 p-3 rounded-lg border"
                        style={{ 
                          backgroundColor: `${primaryColor}10`, 
                          borderColor: `${primaryColor}30` 
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" style={{ color: primaryColor }} />
                          <span className="font-medium">Selected: {selectedSlot.display_time}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Custom Questions for Scheduling */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Documents Step */}
            {steps[currentStep - 1]?.key === 'documents' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{ color: primaryColor }} />
                  Documents & Additional Notes
                </h3>

                {/* Document Upload */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Upload Documents (Optional)</Label>
                  <p className="text-sm text-muted-foreground">
                    Max {intakeInfo.max_files} files. Allowed: {intakeInfo.allowed_file_types.map(t => `.${t}`).join(', ')}
                  </p>
                  
                  <div 
                    className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center transition-colors cursor-pointer"
                    style={{
                      ':hover': {
                        borderColor: `${primaryColor}66`
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = `${primaryColor}66`;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept={intakeInfo.allowed_file_types.map(t => `.${t}`).join(',')}
                    />
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium">Click to select files</p>
                  </div>

                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      {selectedFiles.map((file, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <File className="w-5 h-5" style={{ color: primaryColor }} />
                            <div>
                              <p className="font-medium text-sm">{file.name}</p>
                              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeSelectedFile(i)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="additional_notes">Additional Notes</Label>
                  <Textarea
                    id="additional_notes"
                    value={formData.additional_notes}
                    onChange={(e) => updateFormData('additional_notes', e.target.value)}
                    placeholder="Anything else you'd like us to know?"
                    rows={4}
                  />
                </div>

                {/* Summary */}
                <div 
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: `${primaryColor}10`, 
                    borderColor: `${primaryColor}30` 
                  }}
                >
                  <h4 className="font-semibold mb-3" style={{ color: primaryColor }}>Ready to Submit</h4>
                  <div className="text-sm space-y-1" style={{ color: `${primaryColor}CC` }}>
                    <p>• Contact: {formData.first_name} {formData.last_name}</p>
                    <p>• Case Type: {intakeInfo.case_types.find(t => t.id === formData.case_type)?.name || 'Not selected'}</p>
                    <p>• Documents: {selectedFiles.length} file(s)</p>
                    {intakeInfo.scheduling_enabled && selectedSlot && (
                      <p>• Appointment: {format(selectedDate, 'MMM d, yyyy')} at {selectedSlot.display_time}</p>
                    )}
                  </div>
                </div>
                
                {/* Custom Questions for Documents */}
                {renderCustomQuestions()}
              </div>
            )}

            {/* Consent Checkbox - Show on last step */}
            {currentStep === totalSteps && intakeInfo?.require_consent !== false && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="w-5 h-5 mt-0.5 rounded"
                    style={{ accentColor: primaryColor }}
                  />
                  <span className="text-sm">
                    {intakeInfo?.consent_checkbox_text || "I consent to the collection and processing of my information for the purpose of legal consultation."}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </label>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
              ) : <div />}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  style={{ backgroundColor: primaryColor }}
                  className="text-white hover:opacity-90"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={submitIntake} 
                  disabled={submitting || (intakeInfo?.require_consent !== false && !consentChecked)} 
                  style={{ backgroundColor: primaryColor }}
                  className="text-white hover:opacity-90"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Submit Intake Form</>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>Your information is securely transmitted and protected.</p>
        </div>
      </div>
    </div>
  );
}
